<template>
  <div
    :style="debugging ? 'outline: 2px solid green;' : ''"
  >
    <canvas
      ref="canvas"
      width="400"
      height="400"
      :style="debugging ? 'box-shadow: inset 0 0 0 2px red;' : ''"
    />
  </div>
</template>

<script>
import { asyncForEach, averageOfKey, debounce } from '../helpers.js';

const fonts = {
  'caisley': 'Caisley',
};

export default {
  name: 'EartagCanvas',

  props: {
    cropX: {
      default: 0,
      type: Number,
    },

    cropY: {
      default: 0,
      type: Number,
    },

    debugging: {
      type: Boolean,
      default: false,
    },
    fontFamily: {
      type: String,
      default: 'Arial',
    },
    image: {
      type: String,
    },
    lines: {
      type: Array,
    },
  },

  data() {
    return {
      brightness: 0,
      ctx: null,
      errors: {},
      fillColor: 'rgba(0, 0, 0, 0.8)',
      heightCache: {},
      img: null,
      imgPos: {
        height: 400,
        left: 0,
        top: 0,
        width: 400,
      },
    };
  },

  async mounted() {
    this.ctx = this.$refs.canvas.getContext('2d', { willReadFrequently: true });

    this.ctx.canvas.width = 400;
    this.ctx.canvas.height = 400;

    this.loadFonts().then(async () => {
      await this.setupImage()
    });
  },

  created() {
    this.debounceRender = debounce(this.renderCanvas, 500);
  },

  methods: {
    loadFonts() {
      const font = new FontFace('Caisley', 'url(/assets/fonts/OctoberCompressedL-Medium-subset.otf)', {
        style: 'normal',
        weight: 'normal',
      });

      document.fonts.add(font);

      font.load();

      return document.fonts.ready;
    },

    fetchImage() {
      if('image' in this.errors) {
        this.$delete(this.errors, 'image');
      }

      return new Promise((resolve, reject) => {
        // fix cloudfront cache issues
        // https://serverfault.com/questions/856904/chrome-s3-cloudfront-no-access-control-allow-origin-header-on-initial-xhr-req
        let xhr = new XMLHttpRequest();
            xhr.open('GET', this.image + '?x-request=xhr');
            xhr.responseType = 'blob';

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.response);
          } else {
            reject({
              status: xhr.status,
              statusText: xhr.statusText
            });
          }
        };

        xhr.onerror = () => reject({
          status: xhr.status,
          statusText: xhr.statusText
        });

        xhr.send();
      });
    },

    getImageBrightness() {
      let color;
      let colors = [];

      this.offscreen().clearRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height);

      this.offscreen().drawImage(this.img, this.imgPos.left, this.imgPos.top, this.imgPos.width, this.imgPos.height);

      const imageData = this.offscreen().getImageData(
        this.ctx.canvas.width / 2 - 1,
        this.ctx.canvas.height / 2 + 15 - 1,
        2,
        2
      );

      for(let x = 0, len = imageData.data.length; x < len; x += 4) {
        colors.push({
          r: imageData.data[x],
          g: imageData.data[x+1],
          b: imageData.data[x+2],
          a: imageData.data[x+3],
        });
      }

      color = {
        r: averageOfKey(colors, 'r'),
        g: averageOfKey(colors, 'g'),
        b: averageOfKey(colors, 'b')
      };

      return (this.brightness = Math.round(0.2126 * color.r + 0.7152 * color.g + 0.0722 * color.b));
    },

    offscreen() {
      if(!this.offscreenCanvas) {
        this.offscreenCanvas = document.createElement('canvas');
        this.offscreenCanvas.height = this.ctx.canvas.height;
        this.offscreenCanvas.width = this.ctx.canvas.width;
      }

      return this.offscreenCanvas.getContext('2d', { willReadFrequently: true });
    },

    async renderCanvas() {
      this.offscreen().clearRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height);

      if(this.img !== null) {
        this.offscreen().drawImage(this.img, this.imgPos.left, this.imgPos.top, this.imgPos.width, this.imgPos.height);

        if(this.debugging) {
          this.offscreen().strokeStyle = '#00F';
          this.offscreen().lineWidth = 2;
          this.offscreen().strokeRect(
            this.imgPos.left + 2,
            this.imgPos.top + 2,
            this.imgPos.width - 4,
            this.imgPos.height - 4
          );
        }
      }

      await asyncForEach(this.lines, async (row) => {
        row = (row || {});

        if(row.type === 'image') {
          await this.renderImage(row);
        } else if(row.type !== 'blank') {
          this.writeRow(row.value || '', row.width, row.height, row.x, row.y, null, row.font);
        }
      }, this);

      if(this.debugging) {
        this.offscreen().strokeStyle = '#000';
        this.offscreen().lineWidth = 2;
        this.offscreen().strokeRect(
          this.ctx.canvas.width / 2 - 2,
          this.ctx.canvas.height / 2 + 15 - 2,
          4,
          4
        );
      }

      if(this.cropX > 0) {
        let pxX = this.ctx.canvas.width * (this.cropX / 100);

        this.offscreen().beginPath();
        this.offscreen().setLineDash([10, 5]);
        this.offscreen().moveTo(pxX, 0);
        this.offscreen().lineTo(pxX, this.ctx.canvas.height);
        this.offscreen().moveTo(this.ctx.canvas.width - pxX, 0);
        this.offscreen().lineTo(this.ctx.canvas.width - pxX, this.ctx.canvas.height);
        this.offscreen().strokeStyle = 'magenta';
        this.offscreen().lineWidth = 2;
        this.offscreen().stroke();
      }

      if(this.cropY > 0) {
        let pxY = this.ctx.canvas.height * (this.cropY / 100);

        this.offscreen().beginPath();
        this.offscreen().setLineDash([10, 5]);
        this.offscreen().moveTo(0, pxY);
        this.offscreen().lineTo(this.ctx.canvas.width, pxY);
        this.offscreen().moveTo(0, this.ctx.canvas.height - pxY);
        this.offscreen().lineTo(this.ctx.canvas.width, this.ctx.canvas.height - pxY);
        this.offscreen().strokeStyle = 'magenta';
        this.offscreen().lineWidth = 2;
        this.offscreen().stroke();
      }

      this.ctx.clearRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height);
      this.ctx.drawImage(this.offscreen().canvas, 0, 0);

      this.offscreen().clearRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height);

      this.$emit('rendered');
    },

    async renderImage(row) {
      const img = new Image(),
        svgSrc = '<svg style="color: ' + this.fillColor + ';" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><path fill="currentColor" d="M22.6 21.8H26l3.2 5.4 3.2-5.4h3.4l-5.1 8.4v5.3h-3v-5.3l-5.1-8.4zM35.9 25.8c.3-.9.7-1.6 1.3-2.3.6-.7 1.3-1.2 2.1-1.5.8-.4 1.8-.6 2.8-.6 1.1 0 2 .2 2.8.6.8.4 1.5.9 2.1 1.5.6.7 1 1.4 1.3 2.3s.5 1.8.5 2.8c0 1-.2 1.9-.5 2.8-.3.9-.7 1.6-1.3 2.3-.6.6-1.3 1.1-2.1 1.5-.8.4-1.8.5-2.8.5-1 0-2-.2-2.8-.5s-1.5-.9-2.1-1.5c-.6-.6-1-1.4-1.3-2.3-.3-.9-.5-1.8-.5-2.8.1-1 .2-1.9.5-2.8zm2.8 4.5c.1.5.3 1 .6 1.5.3.4.7.8 1.1 1s1 .4 1.7.4 1.3-.1 1.7-.4.9-.6 1.1-1c.3-.4.5-.9.6-1.5.1-.5.2-1.1.2-1.7 0-.6-.1-1.2-.2-1.7-.1-.6-.3-1.1-.6-1.5s-.7-.8-1.1-1.1-1-.4-1.7-.4-1.3.1-1.7.4-.9.6-1.1 1.1-.5.9-.6 1.5c-.1.6-.2 1.1-.2 1.7 0 .6 0 1.2.2 1.7zM60.9 34.4c-1 .9-2.5 1.4-4.3 1.4-1.9 0-3.3-.4-4.3-1.3s-1.5-2.3-1.5-4.1v-8.5h3v8.5c0 .4 0 .7.1 1.1.1.4.2.7.4.9.2.3.5.5.9.7.4.2.9.3 1.5.3 1.1 0 1.8-.2 2.2-.7.4-.5.6-1.2.6-2.3v-8.5h3v8.5c0 1.7-.5 3.1-1.6 4zM72.5 21.8c.6 0 1.2.1 1.7.3.5.2.9.5 1.3.8.4.3.6.7.8 1.2.2.5.3.9.3 1.5 0 .8-.2 1.5-.5 2.1-.3.6-.9 1-1.7 1.3.4.1.7.3.9.5.2.2.4.5.6.7.2.3.3.6.3.9.1.3.1.7.1 1v.8c0 .3 0 .6.1.9 0 .3.1.6.2.9.1.3.2.5.3.7h-3c-.2-.4-.3-1-.3-1.6 0-.6-.1-1.2-.2-1.7-.1-.7-.3-1.2-.7-1.6-.3-.3-.9-.5-1.6-.5h-3v5.3h-3V21.8h7.4zM71.4 28c.7 0 1.2-.2 1.6-.5.3-.3.5-.8.5-1.5s-.2-1.1-.5-1.4c-.3-.3-.9-.5-1.6-.5h-3.3V28h3.3zM27.1 43.3v11.2h6.7V57h-9.7V43.3h3zM35 47.3c.3-.9.7-1.6 1.3-2.3.6-.7 1.3-1.2 2.1-1.5.8-.4 1.8-.6 2.8-.6 1.1 0 2 .2 2.8.6.8.4 1.5.9 2.1 1.5.6.7 1 1.4 1.3 2.3s.5 1.8.5 2.8c0 1-.2 1.9-.5 2.8-.3.9-.7 1.6-1.3 2.3-.6.6-1.3 1.1-2.1 1.5-.8.4-1.8.5-2.8.5-1 0-2-.2-2.8-.5s-1.5-.9-2.1-1.5c-.6-.6-1-1.4-1.3-2.3-.3-.9-.5-1.8-.5-2.8 0-1 .2-1.9.5-2.8zm2.8 4.5c.1.5.3 1 .6 1.5.3.4.7.8 1.1 1s1 .4 1.7.4 1.3-.1 1.7-.4.9-.6 1.1-1c.3-.4.5-.9.6-1.5.1-.5.2-1.1.2-1.7 0-.6-.1-1.2-.2-1.7-.1-.6-.3-1.1-.6-1.5s-.7-.8-1.1-1.1-1-.4-1.7-.4-1.3.1-1.7.4-.9.6-1.1 1.1-.5.9-.6 1.5c-.1.6-.2 1.1-.2 1.7 0 .6 0 1.2.2 1.7zM58.1 56.8c-.7.3-1.3.4-2 .4-1 0-2-.2-2.8-.5s-1.5-.9-2.1-1.5c-.6-.6-1-1.4-1.3-2.3-.3-.9-.5-1.8-.5-2.8 0-1 .2-2 .5-2.8s.7-1.6 1.3-2.3c.6-.7 1.3-1.2 2.1-1.5.8-.4 1.8-.6 2.8-.6.7 0 1.4.1 2 .3.7.2 1.3.5 1.8.9s1 .9 1.3 1.5.6 1.3.6 2.1H59c-.2-.8-.5-1.3-1-1.7-.5-.4-1.1-.6-1.9-.6-.7 0-1.3.1-1.7.4s-.9.6-1.2 1.1-.5.9-.6 1.5c-.1.6-.2 1.1-.2 1.7 0 .6.1 1.1.2 1.7.1.5.3 1 .6 1.5.3.4.7.8 1.2 1s1 .4 1.7.4c1 0 1.8-.3 2.3-.8.5-.5.9-1.2.9-2.2h-3v-2.2H62V57h-1.9l-.3-1.6c-.4.7-1 1.2-1.7 1.4zM64.4 47.3c.3-.9.7-1.6 1.3-2.3s1.3-1.2 2.1-1.5c.8-.4 1.8-.6 2.8-.6 1.1 0 2 .2 2.8.6.8.4 1.5.9 2.1 1.5.6.7 1 1.4 1.3 2.3s.5 1.8.5 2.8c0 1-.2 1.9-.5 2.8-.3.9-.7 1.6-1.3 2.3-.6.6-1.3 1.1-2.1 1.5s-1.8.5-2.8.5c-1 0-2-.2-2.8-.5s-1.5-.9-2.1-1.5c-.6-.6-1-1.4-1.3-2.3-.3-.9-.5-1.8-.5-2.8 0-1 .2-1.9.5-2.8zm2.7 4.5c.1.5.3 1 .6 1.5.3.4.7.8 1.2 1s1 .4 1.7.4 1.3-.1 1.7-.4.9-.6 1.1-1c.3-.4.5-.9.6-1.5.1-.5.2-1.1.2-1.7 0-.6-.1-1.2-.2-1.7-.1-.6-.3-1.1-.6-1.5s-.7-.8-1.1-1.1-1-.4-1.7-.4-1.3.1-1.7.4-.9.6-1.2 1.1-.5.9-.6 1.5c-.1.6-.2 1.1-.2 1.7 0 .6.1 1.2.2 1.7zM28.5 64.9v5.3H34v-5.3h3v13.7h-3v-5.9h-5.5v5.9h-3V64.9h3zM50 64.9v2.5h-7.2v2.9h6.6v2.3h-6.6V76h7.4v2.5H39.7V64.9H50zM59.6 64.9c.6 0 1.2.1 1.7.3s.9.5 1.3.8.6.7.8 1.2c.2.5.3.9.3 1.5 0 .8-.2 1.5-.5 2.1-.3.6-.9 1-1.7 1.3.4.1.7.3.9.5.2.2.4.5.6.7.2.3.3.6.3.9s.1.7.1 1v.8c0 .3 0 .6.1.9 0 .3.1.6.2.9s.2.5.3.7h-3c-.2-.4-.3-1-.3-1.6 0-.6-.1-1.2-.2-1.7-.1-.7-.3-1.2-.7-1.6-.3-.3-.9-.5-1.6-.5h-3v5.3h-3V64.9h7.4zm-1.1 6.2c.7 0 1.2-.2 1.6-.5.3-.3.5-.8.5-1.5s-.2-1.1-.5-1.4c-.3-.3-.9-.5-1.6-.5h-3.3v3.9h3.3zM76.3 64.9v2.5H69v2.9h6.6v2.3H69V76h7.4v2.5H66V64.9h10.3z"/><path fill="currentColor" d="M50 4c25.4 0 46 20.6 46 46S75.4 96 50 96 4 75.4 4 50 24.6 4 50 4m0-4C22.4 0 0 22.4 0 50s22.4 50 50 50 50-22.4 50-50S77.6 0 50 0z"/></svg>';

      return new Promise((resolve, reject) => {
        img.onload = (e) => {
          const ratio = e.target.width / e.target.height,
            y = row.y;

          let height = row.height,
            width = height * ratio,
            x = row.x;

          if(width > this.img.width) {
            width = this.img.width;
            height = width * (e.target.height / e.target.width);
          }

          this.offscreen().drawImage(e.target, x, y, width, height);

          resolve();
        };
        img.onerror = reject;

        img.src = 'data:image/svg+xml;charset=utf-8,'+svgSrc;
      });
    },

    async setupImage() {
      this.img = new Image();

      this.img.onload = () => {
        URL.revokeObjectURL(this.img.src);

        let height = this.ctx.canvas.height,
            left = 0,
            top = 0,
            width = this.ctx.canvas.width;

        if(this.img.width > this.img.height) {
          height = (this.img.height / this.img.width) * height;
          top = Math.round(this.ctx.canvas.height / 2 - height / 2)
        } else {
          width = (this.img.width / this.img.height) * width;
          left = Math.round(this.ctx.canvas.width / 2 - width / 2);
        }

        this.imgPos = {
          height: Math.round(height),
          left: Math.round(left),
          top: Math.round(top),
          width: Math.round(width),
        };

        this.fillColor = this.getImageBrightness() < 60
                          ? 'rgba(150, 150, 150, 0.8)'
                          : 'rgba(0, 0, 0, 0.8)';

        this.renderCanvas();
      };

      await this.fetchImage().then(response => {

        this.img.src = URL.createObjectURL(response);

      }).catch(error => {
        let msg = "Ear tag preview could not be loaded.";

        window.console.log('Image load error', error);

        this.errors = Object.assign({}, this.errors, {'image': msg});
      });
    },

    writeRow(text, width, height, x, y, align, font) {
      let lines = [];

      if(!text || text === '') {
        return;
      }

      if(text.includes('\n') !== false) {
        lines = text.split('\n');
        text = lines[0];
      }

      const drawFont = font && fonts.hasOwnProperty(font) ? fonts[font] : this.fontFamily;

      const size = height * 1.05;

      this.offscreen().font = "bold " + size + "px " + drawFont;
      if(font === 'caisley') {
        this.offscreen().letterSpacing = '2px';
      }

      this.offscreen().textBaseline = align || 'alphabetic';
      this.offscreen().textAlign = 'center';
      this.offscreen().fillStyle = this.fillColor;

      this.offscreen().fillText(text, x, y, width);

      if(lines.length) {
        this.writeRow(lines[1], width, height * 1.6, x, (y + height) * 1.08, align, font);
      }
    },
  },

  watch: {
    async image() {
      await this.setupImage();
    },

    cropX: {
      handler() {
        this.debounceRender();
      },
    },

    cropY: {
      handler() {
        this.debounceRender();
      },
    },

    lines: {
      deep: true,
      handler() {
        this.debounceRender();
      },
    },
  },
}
</script>

<style scoped>
  canvas {
    background: transparent;
    display: block;
    margin: auto;
    max-height: 100%;
    max-width: 100%;
  }
</style>
